/**
 * Weed & Feed calculator engine — pure functions, no side effects.
 *
 * Composes two existing ideas: herbicide-timing's region → window → urgency
 * model, and the fertilizer bag-count model. Given a ZIP, a product, and a
 * lawn size (optionally a grass type), it returns:
 *   - a safety verdict (which grass types a product's herbicide can burn),
 *   - the spring + fall application windows with urgency,
 *   - how much product to buy (bags, or quarts for the liquid),
 *   - spreader settings and label constraints,
 *   - the best product recommendation.
 *
 * Date handling deliberately does NOT reuse herbicide-timing's
 * parseWindow*Date helpers: those read `new Date()` internally (not
 * injectable) and parse free-text season strings. This engine works from
 * discrete "Month Day" pairs and takes an injectable `referenceDate`, so it
 * is deterministically testable. It replicates the same 6-month-grace
 * year-shift rule (herbicide-timing-calculator.ts adjustWindowOption) so a
 * window that ended months ago rolls to next year instead of reporting a
 * stale negative countdown.
 */

import {
  getRegionFromZipCode,
  toLegacyRegion,
} from '@/data/regional-climate'
import {
  getWeedAndFeedProduct,
  getRegionTiming,
  WEED_AND_FEED_PRODUCTS,
  GRASS_DISPLAY,
  type GrassId,
  type WeedAndFeedProduct,
  type WeedFeedSpreaderSetting,
} from '@/data/weed-and-feed-products'

export type WeedFeedUrgency = 'upcoming' | 'apply-now' | 'running-late' | 'too-late'

export interface WeedFeedWindow {
  label: 'spring' | 'fall'
  displayLabel: string
  window: string
  startDate: Date
  endDate: Date
  urgency: WeedFeedUrgency
  note: string
}

export interface WeedFeedSafety {
  safe: boolean
  /** true when no grass was provided: caution, but results still show. */
  warnOnly: boolean
  reason?: string
  alternativeProductIds: string[]
}

export type WeedFeedAmount =
  | {
      kind: 'granular'
      bagCoverageSqFt: number
      bagWeightLbs: number
      bagsNeeded: number
      totalCoverageSqFt: number
      leftoverSqFt: number
    }
  | {
      kind: 'liquid'
      totalConcentrateOz: number
      containersNeeded: number
      containerLabel: string
      gallonsOfMix: number
      mixText: string
    }

export interface WeedAndFeedInput {
  zipCode: string
  productId: string
  lawnSizeSqFt: number
  grassType?: GrassId | null
  /** Injectable "now" for deterministic timing/urgency. Defaults to new Date(). */
  referenceDate?: Date
}

export interface WeedAndFeedResult {
  region: string
  legacyRegion: string
  regionKnown: boolean
  product: WeedAndFeedProduct
  safety: WeedFeedSafety
  windows: WeedFeedWindow[]
  amount: WeedFeedAmount | null
  spreaderSettings: WeedFeedSpreaderSetting[] | null
  constraints: string[]
  recommendation: { productId: string; asin: string | null; name: string; why: string }
}

const MONTHS: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
}

const DAY_MS = 24 * 60 * 60 * 1000

/** Parse a "Month Day" string (e.g. "May 1") to a Date in the given year. */
function parseMonthDay(monthDay: string, year: number): Date | null {
  const parts = monthDay.trim().split(/\s+/)
  if (parts.length < 2) return null
  const month = MONTHS[parts[0].toLowerCase()]
  const day = parseInt(parts[1], 10)
  if (month === undefined || isNaN(day)) return null
  return new Date(year, month, day)
}

/**
 * Resolve a [start, end] "Month Day" window to concrete dates relative to
 * `now`, applying the same 6-month-grace year-shift as the herbicide-timing
 * engine: a window that ended more than 6 months ago rolls to next year (the
 * user is planning ahead), but a window that ended 0-6 months ago stays in the
 * current year so it can report as "too late this season".
 */
function resolveWindow(
  pair: [string, string],
  now: Date,
): { start: Date; end: Date } | null {
  const year = now.getFullYear()
  const start = parseMonthDay(pair[0], year)
  const end = parseMonthDay(pair[1], year)
  if (!start || !end) return null

  // Cross-year range (e.g. "November 1" - "February 1"): roll the end forward.
  if (end < start) {
    end.setFullYear(end.getFullYear() + 1)
  }

  const sixMonthsAgo = new Date(now)
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  if (end < sixMonthsAgo) {
    start.setFullYear(start.getFullYear() + 1)
    end.setFullYear(end.getFullYear() + 1)
  }

  return { start, end }
}

/** Urgency from a resolved window, using -14 / +60 day boundaries. */
function urgencyFor(start: Date, end: Date, now: Date): WeedFeedUrgency {
  const t = now.getTime()
  if (t < start.getTime() - 14 * DAY_MS) return 'upcoming'
  if (t <= end.getTime()) return 'apply-now'
  if (t <= end.getTime() + 60 * DAY_MS) return 'running-late'
  return 'too-late'
}

function formatWindow(pair: [string, string]): string {
  return `${pair[0]} – ${pair[1]}`
}

function formatGrassList(grasses: GrassId[]): string {
  const labels = grasses.map((grass) => GRASS_DISPLAY[grass])
  if (labels.length <= 1) return labels[0] ?? 'the listed grass types'
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

/** Pick the granular bag size that minimizes leftover (tie-break: fewer bags). */
function chooseGranularAmount(
  product: WeedAndFeedProduct,
  areaSqFt: number,
): WeedFeedAmount | null {
  if (!product.bagOptions || product.bagOptions.length === 0) return null
  let best: { coverage: number; weight: number; bags: number; leftover: number } | null = null
  for (const bag of product.bagOptions) {
    const bags = Math.max(1, Math.ceil(areaSqFt / bag.coverageSqFt))
    const leftover = bags * bag.coverageSqFt - areaSqFt
    if (
      !best ||
      leftover < best.leftover ||
      (leftover === best.leftover && bags < best.bags)
    ) {
      best = { coverage: bag.coverageSqFt, weight: bag.weightLbs, bags, leftover }
    }
  }
  if (!best) return null
  return {
    kind: 'granular',
    bagCoverageSqFt: best.coverage,
    bagWeightLbs: best.weight,
    bagsNeeded: best.bags,
    totalCoverageSqFt: best.bags * best.coverage,
    leftoverSqFt: best.leftover,
  }
}

function chooseLiquidAmount(
  product: WeedAndFeedProduct,
  areaSqFt: number,
): WeedFeedAmount | null {
  const rate = product.liquidRate
  if (!rate) return null
  const totalConcentrateOz = (rate.ozPer1000SqFt * areaSqFt) / 1000
  const containersNeeded = Math.max(
    1,
    Math.ceil(areaSqFt / rate.coveragePerContainerSqFt),
  )
  const gallonsOfMix = Math.max(1, Math.round(areaSqFt / 1000))
  return {
    kind: 'liquid',
    totalConcentrateOz: Math.round(totalConcentrateOz * 10) / 10,
    containersNeeded,
    containerLabel: rate.containerLabel,
    gallonsOfMix,
    mixText: `${rate.ozPer1000SqFt} oz of concentrate per 1,000 sq ft`,
  }
}

export function calculateWeedAndFeed(input: WeedAndFeedInput): WeedAndFeedResult {
  const now = input.referenceDate ?? new Date()
  const product = getWeedAndFeedProduct(input.productId) ?? WEED_AND_FEED_PRODUCTS[0]
  const grass = input.grassType ?? null

  const region = getRegionFromZipCode(input.zipCode)
  const regionKnown = region !== 'Unknown'
  const legacyRegion = toLegacyRegion(region)
  const timing = regionKnown ? getRegionTiming(region) : null

  // ---- Safety (computed first; gates the recommendation) ----
  let safety: WeedFeedSafety
  if (grass && !product.safeGrassTypes.includes(grass)) {
    const alternatives = WEED_AND_FEED_PRODUCTS.filter((p) =>
      p.safeGrassTypes.includes(grass),
    ).map((p) => p.id)
    safety = {
      safe: false,
      warnOnly: false,
      reason: `${product.name} is not labeled for ${GRASS_DISPLAY[grass]}. It is labeled for ${formatGrassList(product.safeGrassTypes)}. Switch to a safe product below.`,
      alternativeProductIds: alternatives,
    }
  } else if (!grass) {
    safety = {
      safe: true,
      warnOnly: true,
      reason: `${product.name} is labeled for ${formatGrassList(product.safeGrassTypes)}. Pick your grass type above to confirm before applying.`,
      alternativeProductIds: [],
    }
  } else {
    safety = { safe: true, warnOnly: false, alternativeProductIds: [] }
  }

  // ---- Timing windows ----
  const windows: WeedFeedWindow[] = []
  if (timing) {
    const springPair: [string, string] =
      product.timingModel === 'pre-emergent' ? timing.preSpring : timing.spring
    const springResolved = resolveWindow(springPair, now)
    if (springResolved) {
      windows.push({
        label: 'spring',
        displayLabel:
          product.timingModel === 'pre-emergent' ? 'Spring (pre-emergent)' : 'Spring',
        window: formatWindow(springPair),
        startDate: springResolved.start,
        endDate: springResolved.end,
        urgency: urgencyFor(springResolved.start, springResolved.end, now),
        note:
          product.timingModel === 'pre-emergent'
            ? 'Before soil hits 55°F, ahead of crabgrass germination'
            : 'After your 2nd-3rd mow, while weeds are actively growing',
      })
    }
    const fallResolved = resolveWindow(timing.fall, now)
    if (fallResolved) {
      windows.push({
        label: 'fall',
        displayLabel: 'Fall',
        window: formatWindow(timing.fall),
        startDate: fallResolved.start,
        endDate: fallResolved.end,
        urgency: urgencyFor(fallResolved.start, fallResolved.end, now),
        note: 'Second pass while weeds store energy for winter',
      })
    }
  }

  // ---- Amount ----
  const area = Number.isFinite(input.lawnSizeSqFt) ? Math.max(0, input.lawnSizeSqFt) : 0
  const amount =
    product.form === 'liquid'
      ? chooseLiquidAmount(product, area)
      : chooseGranularAmount(product, area)

  // ---- Recommendation (swap to a safe product when the pick is unsafe) ----
  const recProduct =
    !safety.safe && safety.alternativeProductIds.length > 0
      ? getWeedAndFeedProduct(safety.alternativeProductIds[0]) ?? product
      : product

  return {
    region,
    legacyRegion,
    regionKnown,
    product,
    safety,
    windows,
    amount,
    spreaderSettings: product.spreaderSettings ?? null,
    constraints: product.constraints,
    recommendation: {
      productId: recProduct.id,
      asin: recProduct.asin,
      name: recProduct.name,
      why: recProduct.why,
    },
  }
}
